import { CustomGripperTooltipConfiguration } from './CustomGripperConfiguration';

/**
 * A custom gripper's tooltip list, read as slots rather than as a sequence.
 * A `null` entry is a slot whose tooltip was deleted; the slots around it
 * keep their numbers.
 */
export type CustomGripperTooltipSlots =
  Array<CustomGripperTooltipConfiguration | null>;

export type OccupiedTooltipSlot = {
  slot: number;
  tooltip: CustomGripperTooltipConfiguration;
};

/**
 * Every slot that holds a tooltip, paired with its slot number.
 *
 * The one place the "an empty slot is not a tooltip" rule lives, so a picker,
 * a count and the option enumeration cannot disagree about whether a deleted
 * tooltip is still there.
 */
export function occupiedTooltipSlots(
  slots: CustomGripperTooltipSlots,
): OccupiedTooltipSlot[] {
  return slots.flatMap((tooltip, slot) =>
    tooltip == null ? [] : [{ slot, tooltip }],
  );
}

/**
 * Empty one slot, leaving every other slot exactly where it was.
 *
 * The list keeps its length, including when the emptied slot is the last one.
 * A slot number is the tooltip's identity everywhere it is stored -- the
 * `ee-CustomGripper-<N>` option name, the `<id>_tcp_<N>` link the URDF mints --
 * and a new tooltip is created at the end of the list, so a list that shrank
 * would hand a slot number back out to a different tooltip.
 */
export function clearTooltipSlot(
  slots: CustomGripperTooltipSlots,
  slot: number,
): CustomGripperTooltipSlots {
  return slots.map((tooltip, index) => (index === slot ? null : tooltip));
}

/**
 * The name a tooltip created in `slot` is given. The ordinal is the slot's,
 * not a count of the tooltips that exist, so a delete cannot leave two
 * tooltips sharing a name that no picker could tell apart.
 */
function defaultTooltipName(slot: number): string {
  return `Tooltip ${slot + 1}`;
}

/**
 * Add a tooltip in a slot no tooltip has ever held.
 *
 * Always past the end, never into an emptied slot: a slot number is spent
 * once it is used, so a stored reference to a deleted tooltip keeps resolving
 * to nothing rather than being adopted by whatever is created next. The name
 * carries that same ordinal, so the add path cannot mint a name it has minted
 * before either.
 *
 * The counterpart to {@link clearTooltipSlot}. Both rules live here because
 * they only hold together: emptying a slot without also allocating past the
 * end would free the number for reuse, which is the bug this encoding exists
 * to close.
 */
export function appendTooltipInNewSlot(
  slots: CustomGripperTooltipSlots,
): CustomGripperTooltipSlots {
  return [
    ...slots,
    CustomGripperTooltipConfiguration.parse({
      name: defaultTooltipName(slots.length),
    }),
  ];
}
